/** Presentation-level derivations shared by the Jobs report columns,
 *  detail panel, summary chips, and minimap. Pure functions. */

export type Severity = 'red' | 'amber' | 'none';
export type DateMode = 'absolute' | 'relative';

/** Released/Firm state chip ("R·F", "R·—", …). Engineered and onHold stay
 *  available as their own boolean fields but are no longer part of the chip. */
export function rfehState(j: {
  released: boolean; firm: boolean; engineered: boolean; onHold: boolean;
}): string {
  return [
    j.released ? 'R' : '—',
    j.firm ? 'F' : '—',
  ].join('·');
}

export function severityOf(j: {
  hasMaterialIssue: boolean; hasLateIssue: boolean; hasDependencyIssue: boolean;
}): Severity {
  if (j.hasMaterialIssue || j.hasLateIssue) return 'red';
  if (j.hasDependencyIssue) return 'amber';
  return 'none';
}

/** Days the earliest feasible start trails the current start. null when unknowable. */
export function daysLate(j: {
  currentStart: string | null; earliestStart: string | null;
}): number | null {
  if (!j.currentStart || !j.earliestStart) return null;
  const a = Date.parse(j.currentStart);
  const b = Date.parse(j.earliestStart);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86_400_000);
}

/** Is a YYYY-MM-DD date strictly before today? Lexicographic comparison is
 *  correct for ISO dates. `today` injected for testability. */
export function isPastDate(date: string | null, today: string): boolean {
  return !!date && date < today;
}

/** Render a YYYY-MM-DD date in absolute or relative-to-today mode.
 *  `today` injected for testability (YYYY-MM-DD). Uses U+2212 minus for negatives. */
export function fmtDateRel(date: string | null, mode: DateMode, today: string): string {
  if (!date) return '—';
  if (mode === 'absolute') return date;
  const d = Math.round((Date.parse(date) - Date.parse(today)) / 86_400_000);
  if (Number.isNaN(d)) return date;
  if (d > 0) return `+${d}d`;
  if (d < 0) return `−${Math.abs(d)}d`;
  return '0d';
}
